
import { useState } from "react";
import { EmailRecord } from "@/types/email";
import { Investor, Contact } from "@/types/investor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import { Mail, Calendar, Check, X } from "lucide-react";
import { updateEmailWithResponse } from "@/utils/emailUtils";
import { getRelevanceBadgeVariant } from "@/utils/uiHelpers";
import { toast } from "sonner";

interface EmailDetailsProps {
  email: EmailRecord;
  investor?: Investor;
  contact?: Contact;
  onClose: () => void;
  onEmailUpdated: (email: EmailRecord) => void;
}

const EmailDetails = ({ 
  email, 
  investor, 
  contact, 
  onClose,
  onEmailUpdated
}: EmailDetailsProps) => {
  const [responseNotes, setResponseNotes] = useState(email.responseNotes || "");
  const [hasResponse, setHasResponse] = useState(email.hasResponse);
  
  const handleUpdateResponse = () => {
    if (hasResponse && !responseNotes.trim()) {
      toast.error("Please add response notes");
      return;
    }
    
    const updatedEmail = updateEmailWithResponse(email.id, hasResponse, responseNotes);
    
    if (updatedEmail) {
      toast.success("Email response updated");
      onEmailUpdated(updatedEmail);
    } else {
      toast.error("Failed to update email response");
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">{email.subject || "Email Details"}</h2>
          <p className="text-gray-500">
            Sent on {format(new Date(email.sentAt), "MMMM d, yyyy 'at' h:mm a")}
          </p>
        </div>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Investor Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {investor && (
              <>
                <div>
                  <p className="text-sm font-medium text-gray-500">Firm</p>
                  <p className="flex items-center gap-2">
                    {investor.firm}
                    <Badge variant={getRelevanceBadgeVariant(investor.relevanceScore)}>
                      {investor.relevanceScore.toFixed(1)}
                    </Badge>
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Investment Focus</p>
                  <p>{investor.investmentFocus || "Not available"}</p>
                </div>
              </>
            )}
            
            {contact && (
              <>
                <div>
                  <p className="text-sm font-medium text-gray-500">Contact</p>
                  <p>{contact.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Role</p>
                  <p>{contact.role || "Not specified"}</p>
                </div>
                {contact.email && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Email</p>
                    <p>{contact.email}</p>
                  </div>
                )}
              </>
            )}
            
            <div>
              <p className="text-sm font-medium text-gray-500">Sent Via</p>
              <p className="capitalize">{email.sentVia || "Manual"}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Response Tracking</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Button 
                variant={hasResponse ? "default" : "outline"} 
                className="flex items-center gap-2"
                onClick={() => setHasResponse(true)}
              >
                <Check className="h-4 w-4" />
                Has Responded
              </Button>
              <Button 
                variant={!hasResponse ? "default" : "outline"} 
                className="flex items-center gap-2"
                onClick={() => setHasResponse(false)}
              >
                <X className="h-4 w-4" />
                No Response
              </Button>
            </div>
            
            <div>
              <p className="text-sm font-medium mb-1">Response Notes</p>
              <Textarea 
                value={responseNotes}
                onChange={(e) => setResponseNotes(e.target.value)}
                placeholder="Add notes about the response here..."
                className="min-h-[100px]"
              />
            </div>
            
            <Button 
              onClick={handleUpdateResponse}
              className="w-full"
            >
              Update Response
            </Button>
            
            {email.responseAt && (
              <div className="flex items-center text-sm text-gray-500">
                <Calendar className="h-3 w-3 mr-1" />
                Response recorded on {format(new Date(email.responseAt), "MMMM d, yyyy")}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Email Content</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 p-4 rounded border whitespace-pre-wrap font-mono text-sm">
            {email.body}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailDetails;
